import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { supabase, SUPABASE_ANON_KEY, SUPABASE_URL } from './supabaseClient';

export function extractEmailDomain(email: string): string {
  const normalized = (email || '').trim().toLowerCase();
  const at = normalized.lastIndexOf('@');
  return at > 0 ? normalized.slice(at + 1) : '';
}

export async function getCurrentUserEmailDomain(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  const email = session?.user?.email ?? '';
  const domain = extractEmailDomain(email);
  return domain || null;
}

export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signUp(email: string, password: string) {
  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function resetPasswordForEmail(email: string, redirectTo?: string) {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/reset-password-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({
      email,
      redirectTo: redirectTo ?? `${window.location.origin}/set-password`,
    }),
  });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload?.error || 'Failed to send reset email.');
}

export async function updatePassword(newPassword: string) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

/**
 * Invite an employee via Edge Function (server-side).
 */
export async function inviteEmployee(
  email: string,
  opts?: { firstName?: string; lastName?: string; name?: string; role?: 'admin' | 'user'; team?: string }
): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) throw new Error('Authentication required to invite employees.');

  try {
    // Use direct fetch to avoid intermittent auth header issues with invoke().
    const response = await fetch(`${SUPABASE_URL}/functions/v1/invite-employee`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        email,
        firstName: opts?.firstName,
        lastName: opts?.lastName,
        name: opts?.name ?? email,
        role: opts?.role,
        team: opts?.team,
      }),
    });

    const payload = await response.json();
    if (!response.ok) throw new Error(payload?.error || 'Failed to invite employee.');
    return (payload as { userId: string }).userId;
  } catch (edgeFnError) {
    throw edgeFnError;
  }
}

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export function onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void) {
  return supabase.auth.onAuthStateChange(callback);
}
